import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NetworkService } from './network-service';
import { ToastService } from './toast.service';
import { LayoutComputationService } from './layout-computation-service';

/**
 * Why a failed lazy route stays broken: the Router drops its failed loader and calls `import()`
 * again, but the browser keeps the failed module fetch in the page's module map, so the same
 * chunk URL keeps rejecting until the document is reloaded.
 */
type ChunkFailureKind =
  | 'offline'       // browser reports no connection
  | 'unreachable'   // online, but the chunk request failed at the network level
  | 'available'     // chunk is served now; the import failure is the browser's cached result
  | 'missing'       // chunk no longer exists (new deployment / stale build)
  | 'server-error'  // server answered with an error status
  | 'unknown';      // no probe-able chunk URL in the error (e.g. Safari's message)

interface PendingNavigation {
  url: string;
  softRetried: boolean;
}

/**
 * Recovers navigation after a lazy-loaded route chunk fails to load (NavigationError carrying a
 * dynamic-import error). Network failures are retried once connectivity returns; failures that need
 * a fresh document (cached failed import, or a chunk missing after a deployment) offer a
 * user-confirmed reload, at most once per URL. Every other navigation error is left untouched.
 */
@Injectable({
  providedIn: 'root'
})
export class LazyLoadRecoveryService {
  /** URL a recovery reload was started for; cleared once that URL loads, blocks a second reload. */
  private static readonly RELOAD_GUARD_KEY = 'lazyLoadRecovery:reloadedUrl';
  private static readonly DYNAMIC_IMPORT_ERROR =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed/i;
  private static readonly CHUNK_URL = /(https?:\/\/[^\s'"]+?\.m?js)(?=[\s'"?#]|$)/i;

  private pending: PendingNavigation | null = null;
  private initialized = false;

  constructor(
    private router: Router,
    private location: Location,
    private networkService: NetworkService,
    private toastService: ToastService,
    private layoutService: LayoutComputationService
  ) { }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.onNavigationSucceeded(event.urlAfterRedirects);
      } else if (event instanceof NavigationError && this.isDynamicImportError(event.error)) {
        void this.handleChunkFailure(event.url, event.error);
      }
    });

    this.networkService.getStatus()
      .pipe(filter((online) => online))
      .subscribe(() => void this.retryPending());
  }

  private async handleChunkFailure(url: string, error: unknown): Promise<void> {
    const kind = await this.classify(this.extractChunkUrl(error));

    switch (kind) {
      case 'offline':
      case 'unreachable':
        // Keep the soft-retry flag when the retry itself failed, so it isn't attempted twice.
        this.pending = { url, softRetried: this.pending?.url === url && this.pending.softRetried };
        this.toastService.showToast({
          type: 'warn',
          summary: 'Connection lost',
          detail: 'This page could not be loaded. It will open when your connection is back.',
          life: 5000
        });
        return;

      case 'available':
      case 'unknown':
        this.pending = null;
        this.offerReload(url, 'transient');
        return;

      case 'missing':
        this.pending = null;
        this.offerReload(url, 'missing');
        return;

      case 'server-error':
        this.pending = null;
        this.toastService.showToast({
          type: 'error',
          summary: 'Error',
          detail: 'This page could not be loaded. Please try again later.'
        });
        return;
    }
  }

  /**
   * Connectivity is back: retry the failed navigation once through the Router, so auth, permission
   * and dirty-form guards still apply. If the browser cached the failed import, the retry fails
   * again and handleChunkFailure classifies it as 'available' (reload offer).
   */
  private async retryPending(): Promise<void> {
    const pending = this.pending;
    if (!pending || pending.softRetried) return;
    pending.softRetried = true;

    await this.router.navigateByUrl(pending.url).catch(() => false);
  }

  private onNavigationSucceeded(url: string): void {
    // The user reached a page (retried or elsewhere): drop any queued retry.
    this.pending = null;

    try {
      if (sessionStorage.getItem(LazyLoadRecoveryService.RELOAD_GUARD_KEY) === url) {
        sessionStorage.removeItem(LazyLoadRecoveryService.RELOAD_GUARD_KEY);
      }
    } catch { }
  }

  private offerReload(url: string, reason: 'transient' | 'missing'): void {
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(LazyLoadRecoveryService.RELOAD_GUARD_KEY) === url;
    } catch { }

    if (alreadyReloaded) {
      // A reload already failed to fix this URL: stop here instead of looping.
      this.toastService.showToast({
        type: 'error',
        summary: 'Error',
        detail: 'This page still could not be loaded. Please refresh your browser or contact support.',
        life: 6000
      });
      return;
    }

    const message = reason === 'missing'
      ? 'A newer version of the application is available. Reload to open this page?'
      : 'This page needs a reload to open after the connection was lost. Reload now?';
    // A reload bypasses the form's unsaved-changes guard, so warn explicitly.
    const unsavedWarning = this.hasUnsavedFormChanges()
      ? '\n\nUnsaved changes on the current form will be lost.'
      : '';

    if (!confirm(message + unsavedWarning)) return;

    try {
      sessionStorage.setItem(LazyLoadRecoveryService.RELOAD_GUARD_KEY, url);
    } catch { }
    window.location.assign(this.location.prepareExternalUrl(url));
  }

  private async classify(chunkUrl: string | null): Promise<ChunkFailureKind> {
    if (!this.networkService.isOnline() || !navigator.onLine) return 'offline';
    if (!chunkUrl) return 'unknown';

    try {
      const response = await fetch(chunkUrl, { method: 'HEAD', cache: 'no-store' });
      if (response.status === 404 || response.status === 410) return 'missing';
      if (!response.ok) return 'server-error';

      // SPA fallbacks answer unknown files with index.html instead of a 404.
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType && !/javascript|ecmascript/i.test(contentType)) return 'missing';
      return 'available';
    } catch {
      return 'unreachable';
    }
  }

  private isDynamicImportError(error: unknown): boolean {
    if (!error) return false;
    if ((error as { name?: unknown }).name === 'ChunkLoadError') return true;
    const message = String((error as { message?: unknown }).message ?? error);
    return LazyLoadRecoveryService.DYNAMIC_IMPORT_ERROR.test(message);
  }

  /** Chunk URL from the error message; only same-origin URLs are probed. */
  private extractChunkUrl(error: unknown): string | null {
    const message = String((error as { message?: unknown })?.message ?? error ?? '');
    const match = message.match(LazyLoadRecoveryService.CHUNK_URL);
    if (!match) return null;

    try {
      const chunkUrl = new URL(match[1]);
      return chunkUrl.origin === window.location.origin ? chunkUrl.href : null;
    } catch {
      return null;
    }
  }

  private hasUnsavedFormChanges(): boolean {
    try {
      return this.router.url.includes('/form-detail') && this.layoutService.isDirty();
    } catch {
      return false;
    }
  }
}
